import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import { withAlpha } from 'utils/colorUtils';

// assets
import {
  IconMail,
  IconMailOpened,
  IconBell,
  IconAlertCircle,
  IconCheck,
  IconX
} from '@tabler/icons-react';

function ListItemWrapper({ children, read, onClick }) {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        bgcolor: read ? 'transparent' : withAlpha(theme.palette.primary.light, 0.08),
        '&:hover': {
          bgcolor: withAlpha(theme.palette.grey[200], 0.3),
          ...theme.applyStyles('dark', { bgcolor: 'dark.900' })
        }
      }}
    >
      {children}
    </Box>
  );
}

// Get icon by notification type/category
function getNotificationIcon(type, category) {
  if (type === 'outbound') {
    return { icon: IconMail, color: 'primary' };
  }
  if (category === 'state_change') {
    return { icon: IconAlertCircle, color: 'warning' };
  }
  if (category === 'observation') {
    return { icon: IconBell, color: 'info' };
  }
  return { icon: IconBell, color: 'secondary' };
}

// Get status chip
function getStatusChip(notification) {
  if (notification.metadata?.status === 'sent') {
    return <Chip label="Sent" color="success" size="small" icon={<IconCheck size={14} />} />;
  }
  if (notification.metadata?.status === 'failed') {
    return <Chip label="Failed" color="error" size="small" icon={<IconX size={14} />} />;
  }
  if (!notification.read) {
    return <Chip label="New" color="warning" size="small" />;
  }
  return null;
}

// ==============================|| NOTIFICATION LIST ||============================== //

export default function NotificationList({ notifications = [], onItemClick, formatters }) {
  const theme = useTheme();

  if (notifications.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <IconMailOpened size={48} stroke={1} color={theme.palette.grey[400]} />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
          No notifications yet
        </Typography>
        <Typography variant="caption" color="text.disabled">
          You'll see alerts here when your Hot Lists find new matches
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: '100%', py: 0 }}>
      {notifications.map((notification) => {
        const { icon: Icon, color } = getNotificationIcon(notification.type, notification.category);
        const timeAgo = notification.created_at
          ? formatters.formatRelative(notification.created_at)
          : '';

        return (
          <ListItemWrapper key={notification.id} read={notification.read} onClick={() => onItemClick?.(notification)}>
            <ListItem alignItems="flex-start" disablePadding>
              <ListItemAvatar sx={{ mt: 0.25 }}>
                <Avatar
                  sx={{
                    color: `${color}.dark`,
                    bgcolor: `${color}.light`,
                    ...theme.applyStyles('dark', { bgcolor: 'dark.main' })
                  }}
                >
                  <Icon stroke={1.5} size="20px" />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                disableTypography
                primary={
                  <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ lineHeight: 1.4, flex: 1 }}>{notification.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.3 }}>{timeAgo}</Typography>
                  </Stack>
                }
                secondary={
                  <Stack sx={{ gap: 0.75 }}>
                    <Typography variant="body2" color="text.secondary">
                      {notification.body}
                    </Typography>
                    <Stack direction="row" sx={{ gap: 1 }}>
                      {getStatusChip(notification)}
                      {notification.type && (
                        <Chip
                          label={notification.type === 'outbound' ? 'Alert' : 'Event'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Stack>
                }
              />
            </ListItem>
          </ListItemWrapper>
        );
      })}
    </List>
  );
}

ListItemWrapper.propTypes = { children: PropTypes.node, read: PropTypes.bool, onClick: PropTypes.func };
NotificationList.propTypes = { notifications: PropTypes.array, onItemClick: PropTypes.func, formatters: PropTypes.object.isRequired };
